import cx from 'classnames';
import './Tabs.css';

/**
 * QHS Tabs
 * --------
 * Horizontal tabs bar with optional per-tab icon, count chip, and
 * alert dot. URL-driven by convention — caller maintains `value`
 * and reacts to `onChange`. Use for page-level navigation between
 * sibling lists / sub-views (Patient Dashboard, future analytics
 * dashboards).
 *
 *   <Tabs
 *     value={activeKey}
 *     onChange={(key) => navigate(buildPath(key))}
 *     items={[
 *       { key: 'assessments', label: 'Assessments', icon: <CheckIcon />, count: 20 },
 *       { key: 'orders', label: 'Orders', count: 3, hasAlert: true },
 *     ]}
 *   />
 *
 * `count` and `hasAlert` are optional. When `count == null` the
 * chip is not rendered. `hasAlert` adds a red dot adjacent to the
 * count for "this tab has critical content" callouts.
 *
 * Variants:
 * - `underline` (default) — flat row with a 1px border-bottom and a 2px
 *   brand-coloured underline on the active tab. Use when the active
 *   tab is directly above the content it controls.
 * - `pill` — pills with a soft brand-tinted background on the active
 *   tab and no underlines. Use when tabs sit inside a card or are
 *   visually disconnected from the content area.
 *
 *   <Tabs variant="pill" value={...} onChange={...} items={...} />
 */
const Tabs = ({ value, onChange, items = [], variant = 'underline', className, ...rest }) => {
  return (
    <div
      role='tablist'
      className={cx(
        'qhs-tabs',
        variant === 'pill' && 'qhs-tabs--pill',
        className,
      )}
      {...rest}
    >
      {items.map((item) => {
        const isActive = item.key === value;
        const showCount = item.count !== null && item.count !== undefined;
        return (
          <button
            key={item.key}
            type='button'
            role='tab'
            aria-selected={isActive}
            onClick={() => onChange?.(item.key)}
            className={cx('qhs-tabs__tab', {
              'qhs-tabs__tab--active': isActive,
              'qhs-tabs__tab--alert': item.hasAlert,
            })}
          >
            {item.icon && <span className='qhs-tabs__icon'>{item.icon}</span>}
            <span className='qhs-tabs__label'>{item.label}</span>
            {showCount && <span className='qhs-tabs__count'>{item.count}</span>}
            {item.hasAlert && <span className='qhs-tabs__alert-dot' aria-hidden='true' />}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
